// ConstruireEvaluation.jsx - Version avec affichage compact des questions
// Support DIAGNOSTIC, FORMATIVE, SOMMATIVE, EXAMEN, CONCOURS
// Inclut les types : CHOIX_UNIQUE, CHOIX_MULTIPLE, VRAI_FAUX, TEXTE_TROU,
// APPARIE, REP_COURT, REP_LONG, CLASSE, CATEGORI, AUDIO/FILE
import React, { useState, useCallback, useEffect } from 'react';
import {
    Plus, Trash2, ChevronDown, ChevronRight,
    X, Eye, Save,
    BarChart3, ClipboardList, ChevronsUpDown
} from 'lucide-react';

// Définition des types de questions avec compatibilité
const QUESTION_TYPES = [
    { value: 'CHOIX_UNIQUE', label: 'Choix unique', icon: '🔘', compatible: ['DIAGNOSTIC','FORMATIVE','SOMMATIVE','EXAMEN','CONCOURS'] },
    { value: 'CHOIX_MULTIPLE', label: 'Choix multiple', icon: '☑️', compatible: ['DIAGNOSTIC','FORMATIVE','SOMMATIVE','EXAMEN','CONCOURS'] },
    { value: 'VRAI_FAUX', label: 'Vrai/Faux', icon: '✅', compatible: ['DIAGNOSTIC','FORMATIVE','SOMMATIVE','EXAMEN','CONCOURS'] },
    { value: 'TEXTE_TROU', label: 'Texte à trous', icon: '📝', compatible: ['SOMMATIVE','EXAMEN','CONCOURS'] },
    { value: 'APPARIE', label: 'Appariement', icon: '🔗', compatible: ['SOMMATIVE','EXAMEN','CONCOURS'] },
    { value: 'REP_COURT', label: 'Réponse courte', icon: '✏️', compatible: ['SOMMATIVE','EXAMEN','CONCOURS'] },
    { value: 'REP_LONG', label: 'Réponse longue', icon: '📄', compatible: ['SOMMATIVE','EXAMEN','CONCOURS'] },
    { value: 'CLASSE', label: 'Classement', icon: '📋', compatible: ['SOMMATIVE','EXAMEN','CONCOURS'] },
    { value: 'CATEGORI', label: 'Catégorisation', icon: '🏷️', compatible: ['SOMMATIVE','EXAMEN','CONCOURS'] },
    { value: 'AUDIO/FILE', label: 'Audio/Fichier', icon: '🎵', compatible: ['SOMMATIVE','EXAMEN','CONCOURS'] }
];

export default function ConstruireEvaluation({
    evaluationId = null,
    clientId,
    token,
    initialData = null,
    evalType = 'SOMMATIVE',
    onSave,
    onDelete,
    onPreview,
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showJsonPreview, setShowJsonPreview] = useState(false);
    const [expandedQuestions, setExpandedQuestions] = useState({});
    const [showImportModal, setShowImportModal] = useState(false);
    const [importJson, setImportJson] = useState('');
    const [importError, setImportError] = useState(null);

    // État du type d'évaluation (modifiable par l'utilisateur)
    const [evalTypeState, setEvalTypeState] = useState(evalType);

    // Fonction utilitaire pour générer des IDs uniques
    function generateId(prefix) {
        return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    }

    // Détermine si le type d'évaluation est un examen ou concours (sans solution)
    const isExamenOrConcours = (type) => type === 'EXAMEN' || type === 'CONCOURS';

    // Normalisation des questions selon le type d'évaluation
    const normalizeQuestions = useCallback((questions, type) => {
        if (!questions || !Array.isArray(questions)) return [];
        const isDiag = type === 'DIAGNOSTIC';
        const isExamenConcours = isExamenOrConcours(type);
        return questions.map((q) => {
            const newQ = { ...q };
            if (!newQ.id) newQ.id = generateId('q');
            if (!newQ.points) newQ.points = 1;
            if (!newQ.explication) newQ.explication = '';
            if (newQ.feedbackAuto === undefined) newQ.feedbackAuto = true;

            if (isDiag) {
                if (Array.isArray(newQ.options)) {
                    newQ.options = newQ.options.map(opt => {
                        if (typeof opt === 'string') {
                            return { id: generateId('opt'), texte: opt };
                        } else if (opt && typeof opt === 'object') {
                            return { id: opt.id || generateId('opt'), texte: opt.texte || '' };
                        }
                        return { id: generateId('opt'), texte: '' };
                    });
                } else {
                    newQ.options = [];
                }
                if (Array.isArray(newQ.resultats)) {
                    newQ.resultats = newQ.resultats.join(',');
                } else if (newQ.resultats && typeof newQ.resultats !== 'string') {
                    newQ.resultats = String(newQ.resultats);
                } else if (!newQ.resultats) {
                    newQ.resultats = '';
                }
                if (isExamenConcours) delete newQ.resultats;
                delete newQ.solutions;
            } else {
                if (Array.isArray(newQ.options)) {
                    newQ.options = newQ.options.map(opt => {
                        if (typeof opt === 'string') return opt;
                        if (opt && typeof opt === 'object' && opt.texte) return opt.texte;
                        return '';
                    });
                } else {
                    newQ.options = [];
                }
                if (isExamenConcours) {
                    delete newQ.solutions;
                } else {
                    if (Array.isArray(newQ.solutions)) {
                        newQ.solutions = newQ.solutions.map(s => String(s));
                    } else if (newQ.solutions && typeof newQ.solutions === 'string') {
                        newQ.solutions = [newQ.solutions];
                    } else {
                        newQ.solutions = [];
                    }
                }
                if (newQ.typeQuestion === 'VRAI_FAUX') {
                    newQ.options = ['Vrai', 'Faux'];
                }
                // ⭐ Nettoyage : même si une donnée importée (ancien JSON) contient encore
                // `solutions` pour une question APPARIE, on la purge ici — configurationAvancee
                // reste l'unique source pour ce type.
                if (newQ.typeQuestion === 'APPARIE') {
                    newQ.solutions = [];
                }
                delete newQ.resultats;
            }

            if (!newQ.configurationAvancee) newQ.configurationAvancee = {};

            return newQ;
        });
    }, []);

    // État local de l'évaluation (inclut le clientId)
    const getDefaultEvaluation = useCallback(() => {
        const isDiag = evalTypeState === 'DIAGNOSTIC';
        const isExamenConcours = isExamenOrConcours(evalTypeState);
        return {
            type: evalTypeState,
            titre: isDiag ? 'Évaluation Diagnostique' : 
                   evalTypeState === 'FORMATIVE' ? 'Évaluation Formative' :
                   evalTypeState === 'EXAMEN' ? 'Examen' :
                   evalTypeState === 'CONCOURS' ? 'Concours' : 'Évaluation Sommative',
            code: `EVAL-${Date.now()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`,
            status: 'BROUILLON',
            description: isDiag ? 'Évaluez vos connaissances initiales' : 'Évaluation complète',
            scoreMinimumPourValider: 70,
            clientId: clientId,
            questions: [
                {
                    id: generateId('q'),
                    enonce: 'Question 1',
                    typeQuestion: 'CHOIX_UNIQUE',
                    options: isDiag
                        ? [{ id: generateId('opt'), texte: 'Option A' }, { id: generateId('opt'), texte: 'Option B' }]
                        : ['Option A', 'Option B', 'Option C'],
                    resultats: isDiag ? '' : undefined,
                    solutions: (isDiag || isExamenConcours) ? undefined : ['Option A'],
                    points: 1,
                    explication: '',
                    feedbackAuto: true,
                    configurationAvancee: {}
                }
            ]
        };
    }, [evalTypeState, clientId]);

    const [evalData, setEvalData] = useState(() => {
        if (initialData) {
            const data = typeof initialData === 'string' ? JSON.parse(initialData) : initialData;
            if (!data.clientId && clientId) data.clientId = clientId;
            return data;
        }
        return getDefaultEvaluation();
    });

    // Chargement de l'évaluation si un ID est fourni
    useEffect(() => {
        if (!evaluationId || !token) return;

        const fetchEvaluation = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`/api/v1/admin/evaluations/${evaluationId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                });
                if (!response.ok) {
                    throw new Error(`Erreur HTTP ${response.status}`);
                }
                const data = await response.json();
                const mapped = {
                    ...data,
                    scoreMinimumPourValider: data.scoreMinimum ?? 70,
                    questions: data.questions || []
                };
                mapped.clientId = clientId || data.clientId;
                const normalizedQuestions = normalizeQuestions(mapped.questions, data.type || evalTypeState);
                mapped.questions = normalizedQuestions;
                setEvalData(mapped);
                if (data.type) setEvalTypeState(data.type);
                setSuccess('Évaluation chargée avec succès');
            } catch (err) {
                setError(`Erreur de chargement : ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchEvaluation();
    }, [evaluationId, token, clientId, normalizeQuestions]);

    // Synchronisation du clientId
    useEffect(() => {
        setEvalData(prev => {
            if (prev.clientId !== clientId) {
                return { ...prev, clientId };
            }
            return prev;
        });
    }, [clientId]);

    // ============================================================
    // FONCTIONS UTILES
    // ============================================================
    const getQuestionTypeLabel = (type) => {
        const found = QUESTION_TYPES.find(t => t.value === type);
        return found ? found.label : type;
    };

    const getQuestionTypeIcon = (type) => {
        const found = QUESTION_TYPES.find(t => t.value === type);
        return found ? found.icon : '❓';
    };

    const getAvailableTypes = () => {
        return QUESTION_TYPES.filter(t => t.compatible.includes(evalTypeState));
    };

    const isCurrentExamenOrConcours = isExamenOrConcours(evalTypeState);

    // ============================================================
    // GESTION DES QUESTIONS
    // ============================================================
    const addQuestion = useCallback(() => {
        const newId = generateId('q');
        const isDiag = evalTypeState === 'DIAGNOSTIC';
        const isExamenConcours = isCurrentExamenOrConcours;
        let newQuestion = {
            id: newId,
            enonce: `Question ${evalData.questions.length + 1}`,
            typeQuestion: 'CHOIX_UNIQUE',
            points: 1,
            explication: '',
            feedbackAuto: true,
            configurationAvancee: {}
        };

        if (isDiag) {
            newQuestion.options = [
                { id: generateId('opt'), texte: 'Option A' },
                { id: generateId('opt'), texte: 'Option B' }
            ];
            if (!isExamenConcours) newQuestion.resultats = '';
        } else {
            newQuestion.options = ['Option A', 'Option B', 'Option C'];
            if (!isExamenConcours) newQuestion.solutions = ['Option A'];
        }

        setEvalData(prev => ({
            ...prev,
            questions: [...prev.questions, newQuestion]
        }));
        setExpandedQuestions(prev => ({ ...prev, [newId]: true }));
    }, [evalData.questions, evalTypeState, isCurrentExamenOrConcours]);

    const removeQuestion = useCallback((qIndex) => {
        if (!window.confirm('Supprimer cette question ?')) return;
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.filter((_, idx) => idx !== qIndex)
        }));
    }, []);

    const updateQuestion = useCallback((qIndex, field, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? { ...q, [field]: value } : q
            )
        }));
    }, []);

    // Gestion des options (Diagnostic)
    const updateOptionDiagnostic = useCallback((qIndex, optIndex, field, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    options: q.options.map((o, oi) =>
                        oi === optIndex ? { ...o, [field]: value } : o
                    )
                } : q
            )
        }));
    }, []);

    const addOptionDiagnostic = useCallback((qIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    options: [
                        ...q.options,
                        { id: generateId('opt'), texte: `Option ${q.options.length + 1}` }
                    ]
                } : q
            )
        }));
    }, []);

    const removeOptionDiagnostic = useCallback((qIndex, optIndex) => {
        if (evalData.questions[qIndex]?.options?.length <= 2) {
            alert('Une question doit avoir au moins 2 options');
            return;
        }
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    options: q.options.filter((_, oi) => oi !== optIndex)
                } : q
            )
        }));
    }, [evalData]);

    // Gestion des options (Somative / Formative / Examen / Concours)
    const updateOptionSomative = useCallback((qIndex, optIndex, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    options: q.options.map((o, oi) =>
                        oi === optIndex ? value : o
                    )
                } : q
            )
        }));
    }, []);

    const addOptionSomative = useCallback((qIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    options: [...q.options, `Option ${q.options.length + 1}`]
                } : q
            )
        }));
    }, []);

    const removeOptionSomative = useCallback((qIndex, optIndex) => {
        if (evalData.questions[qIndex]?.options?.length <= 2) {
            alert('Une question doit avoir au moins 2 options');
            return;
        }
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    options: q.options.filter((_, oi) => oi !== optIndex)
                } : q
            )
        }));
    }, [evalData]);

    // Gestion des solutions
    const updateSolution = useCallback((qIndex, solIndex, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    solutions: q.solutions.map((s, si) =>
                        si === solIndex ? value : s
                    )
                } : q
            )
        }));
    }, []);

    const addSolution = useCallback((qIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    solutions: [...q.solutions, '']
                } : q
            )
        }));
    }, []);

    const removeSolution = useCallback((qIndex, solIndex) => {
        if (evalData.questions[qIndex]?.solutions?.length <= 1) {
            alert('Une question doit avoir au moins 1 solution');
            return;
        }
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    solutions: q.solutions.filter((_, si) => si !== solIndex)
                } : q
            )
        }));
    }, [evalData]);

    // Gestion de configurationAvancee
    const updateConfigurationAvancee = useCallback((qIndex, key, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    configurationAvancee: {
                        ...(q.configurationAvancee || {}),
                        [key]: value
                    }
                } : q
            )
        }));
    }, []);

    const removeConfigurationAvanceeKey = useCallback((qIndex, key) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? {
                    ...q,
                    configurationAvancee: Object.fromEntries(
                        Object.entries(q.configurationAvancee || {}).filter(([k]) => k !== key)
                    )
                } : q
            )
        }));
    }, []);

    // Changement de type de question
    const changeQuestionType = useCallback((qIndex, newType) => {
        const question = evalData.questions[qIndex];
        let newQuestion = { ...question, typeQuestion: newType };
        const isDiag = evalTypeState === 'DIAGNOSTIC';
        const isExamenConcours = isCurrentExamenOrConcours;

        if (isDiag) {
            if (newType === 'CHOIX_UNIQUE' || newType === 'CHOIX_MULTIPLE') {
                if (!newQuestion.options || newQuestion.options.length === 0) {
                    newQuestion.options = [
                        { id: generateId('opt'), texte: 'Option A' },
                        { id: generateId('opt'), texte: 'Option B' }
                    ];
                }
                if (!isExamenConcours && !newQuestion.resultats) {
                    newQuestion.resultats = '';
                }
            }
        } else {
            switch (newType) {
                case 'CHOIX_UNIQUE':
                case 'CHOIX_MULTIPLE':
                    newQuestion.options = ['Option A', 'Option B', 'Option C'];
                    if (!isExamenConcours) newQuestion.solutions = ['Option A'];
                    break;
                case 'VRAI_FAUX':
                    newQuestion.options = ['Vrai', 'Faux'];
                    if (!isExamenConcours) newQuestion.solutions = ['Vrai'];
                    break;
                case 'TEXTE_TROU':
                    newQuestion.options = [];
                    if (!isExamenConcours) newQuestion.solutions = [''];
                    break;
                case 'APPARIE':
                    // ⭐ configurationAvancee est la SEULE source pour cette question
                    // (elle sert à la fois à poser la question et, indirectement, à la corriger).
                    // Le champ `solutions` n'est plus utilisé pour APPARIE afin d'éviter
                    // toute duplication de la question dans un champ censé ne contenir
                    // que la correction.
                    newQuestion.options = [];
                    delete newQuestion.solutions;
                    if (!isExamenConcours) {
                        newQuestion.configurationAvancee = { 'Élément A': 'Élément B' };
                    }
                    break;
                case 'CLASSE':
                    newQuestion.options = ['Élément 1', 'Élément 2'];
                    newQuestion.configurationAvancee = { 'Catégorie 1': ['Élément 1'], 'Catégorie 2': ['Élément 2'] };
                    if (!isExamenConcours) newQuestion.solutions = [];
                    break;
                case 'CATEGORI':
                    newQuestion.options = ['Catégorie A', 'Catégorie B'];
                    newQuestion.configurationAvancee = { 'Élément 1': 'Catégorie A', 'Élément 2': 'Catégorie B' };
                    if (!isExamenConcours) newQuestion.solutions = [];
                    break;
                case 'AUDIO/FILE':
                    newQuestion.options = [];
                    newQuestion.configurationAvancee = { maxFileSize: 5242880, acceptedTypes: ['audio/*'] };
                    if (!isExamenConcours) newQuestion.solutions = [];
                    break;
                case 'REP_COURT':
                case 'REP_LONG':
                    newQuestion.options = [];
                    if (!isExamenConcours) newQuestion.solutions = [''];
                    break;
                default: break;
            }
        }
        if (isExamenConcours) {
            delete newQuestion.solutions;
            delete newQuestion.resultats;
        }

        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) =>
                idx === qIndex ? newQuestion : q
            )
        }));
    }, [evalData, evalTypeState, isCurrentExamenOrConcours]);

    const toggleQuestionExpand = useCallback((qIndex) => {
        setExpandedQuestions(prev => ({
            ...prev,
            [qIndex]: !prev[qIndex]
        }));
    }, []);

    // Tout réduire / Tout étendre
    const toggleAllExpand = useCallback(() => {
        const allExpanded = Object.values(expandedQuestions).every(v => v === true);
        const newState = {};
        evalData.questions.forEach((q, idx) => {
            newState[idx] = !allExpanded;
        });
        setExpandedQuestions(newState);
    }, [expandedQuestions, evalData.questions]);

    // ============================================================
    // RENDU SPÉCIFIQUE PAR TYPE DE QUESTION
    // ============================================================
    const renderSomativeFields = useCallback((question, qIndex) => {
        const isExamenConcours = isCurrentExamenOrConcours;
        const type = question.typeQuestion;

        const renderOptions = (options, addFn, removeFn, updateFn, isObj = false) => (
            <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-700">Options</label>
                {options.map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                        {isObj ? (
                            <>
                                <input
                                    type="text"
                                    value={opt.texte || ''}
                                    onChange={(e) => updateFn(qIndex, idx, 'texte', e.target.value)}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    placeholder={`Option ${idx+1}`}
                                />
                                <button onClick={() => removeFn(qIndex, idx)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                    <Trash2 size={12} />
                                </button>
                            </>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    value={opt}
                                    onChange={(e) => updateFn(qIndex, idx, e.target.value)}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    placeholder={`Option ${idx+1}`}
                                />
                                <button onClick={() => removeFn(qIndex, idx)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                    <Trash2 size={12} />
                                </button>
                            </>
                        )}
                    </div>
                ))}
                <button onClick={() => addFn(qIndex)} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-0.5">
                    <Plus size={10} /> Ajouter une option
                </button>
            </div>
        );

        const renderSolutions = () => (
            <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-700">Solutions (réponses correctes)</label>
                {question.solutions?.map((sol, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                        <input
                            type="text"
                            value={sol}
                            onChange={(e) => updateSolution(qIndex, idx, e.target.value)}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder={`Solution ${idx+1}`}
                        />
                        <button onClick={() => removeSolution(qIndex, idx)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 size={12} />
                        </button>
                    </div>
                ))}
                <button onClick={() => addSolution(qIndex)} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-0.5">
                    <Plus size={10} /> Ajouter une solution
                </button>
                <p className="text-[9px] text-gray-400">Laissez vide pour correction manuelle.</p>
            </div>
        );

        const renderConfigurationAvancee = (keyLabel, valueLabel, obj) => (
            <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-700">Configuration avancée</label>
                {Object.entries(obj).map(([key, val], idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                        <input
                            type="text"
                            value={key}
                            onChange={(e) => {
                                const newObj = { ...obj };
                                delete newObj[key];
                                newObj[e.target.value] = val;
                                updateConfigurationAvancee(qIndex, e.target.value, val);
                            }}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder={keyLabel}
                        />
                        <span className="text-gray-400 text-sm">→</span>
                        <input
                            type="text"
                            value={val}
                            onChange={(e) => updateConfigurationAvancee(qIndex, key, e.target.value)}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder={valueLabel}
                        />
                        <button onClick={() => removeConfigurationAvanceeKey(qIndex, key)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 size={12} />
                        </button>
                    </div>
                ))}
                <button onClick={() => {
                    const newKey = `${keyLabel} ${Object.keys(obj).length + 1}`;
                    updateConfigurationAvancee(qIndex, newKey, '');
                }} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-0.5">
                    <Plus size={10} /> Ajouter une paire
                </button>
            </div>
        );

        switch (type) {
            case 'CHOIX_UNIQUE':
            case 'CHOIX_MULTIPLE':
                return (
                    <div>
                        {renderOptions(question.options, addOptionSomative, removeOptionSomative, updateOptionSomative, false)}
                        {!isExamenConcours && renderSolutions()}
                    </div>
                );
            case 'VRAI_FAUX':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Options (Vrai/Faux)</label>
                            <div className="flex gap-3 mt-1">
                                <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name={`vf-${qIndex}`}
                                        value="Vrai"
                                        checked={question.solutions?.[0] === 'Vrai' || false}
                                        onChange={() => updateQuestion(qIndex, 'solutions', ['Vrai'])}
                                        className="w-3.5 h-3.5 text-green-600 focus:ring-green-500"
                                    />
                                    Vrai
                                </label>
                                <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name={`vf-${qIndex}`}
                                        value="Faux"
                                        checked={question.solutions?.[0] === 'Faux' || false}
                                        onChange={() => updateQuestion(qIndex, 'solutions', ['Faux'])}
                                        className="w-3.5 h-3.5 text-green-600 focus:ring-green-500"
                                    />
                                    Faux
                                </label>
                            </div>
                        </div>
                        {!isExamenConcours && renderSolutions()}
                    </div>
                );
            case 'TEXTE_TROU':
                return (
                    <div>
                        <p className="text-[10px] text-gray-500 mb-1.5">Utilisez ___ dans l'énoncé pour indiquer les trous.</p>
                        {!isExamenConcours && renderSolutions()}
                    </div>
                );
            case 'APPARIE':
                // ⭐ Plus de renderSolutions() ici : pour l'appariement, la question
                // ET la correction viennent uniquement de configurationAvancee.
                // (voir validateEvaluation ci-dessous pour l'obligation de le remplir)
                return (
                    <div>
                        {renderConfigurationAvancee('Clé', 'Valeur', question.configurationAvancee || {})}
                    </div>
                );
            case 'CLASSE':
                return (
                    <div>
                        <label className="block text-xs font-medium text-gray-700">Catégories et éléments à classer</label>
                        {Object.entries(question.configurationAvancee || {}).map(([cat, items]) => (
                            <div key={cat} className="flex items-center gap-1.5 mb-1">
                                <input
                                    type="text"
                                    value={cat}
                                    onChange={(e) => {
                                        const newObj = { ...question.configurationAvancee };
                                        delete newObj[cat];
                                        newObj[e.target.value] = items;
                                        updateConfigurationAvancee(qIndex, e.target.value, items);
                                    }}
                                    className="w-1/3 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    placeholder="Catégorie"
                                />
                                <input
                                    type="text"
                                    value={items.join(', ')}
                                    onChange={(e) => {
                                        const newItems = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                                        updateConfigurationAvancee(qIndex, cat, newItems);
                                    }}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    placeholder="Éléments (séparés par virgules)"
                                />
                                <button onClick={() => removeConfigurationAvanceeKey(qIndex, cat)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                        <button onClick={() => {
                            const catCount = Object.keys(question.configurationAvancee || {}).length + 1;
                            updateConfigurationAvancee(qIndex, `Catégorie ${catCount}`, []);
                        }} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-0.5">
                            <Plus size={10} /> Ajouter une catégorie
                        </button>
                    </div>
                );
            case 'CATEGORI':
                return (
                    <div>
                        <label className="block text-xs font-medium text-gray-700">Éléments et leur catégorie</label>
                        {Object.entries(question.configurationAvancee || {}).map(([elem, cat]) => (
                            <div key={elem} className="flex items-center gap-1.5 mb-1">
                                <input
                                    type="text"
                                    value={elem}
                                    onChange={(e) => {
                                        const newObj = { ...question.configurationAvancee };
                                        delete newObj[elem];
                                        newObj[e.target.value] = cat;
                                        updateConfigurationAvancee(qIndex, e.target.value, cat);
                                    }}
                                    className="w-1/3 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    placeholder="Élément"
                                />
                                <input
                                    type="text"
                                    value={cat}
                                    onChange={(e) => updateConfigurationAvancee(qIndex, elem, e.target.value)}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    placeholder="Catégorie"
                                />
                                <button onClick={() => removeConfigurationAvanceeKey(qIndex, elem)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                        <button onClick={() => {
                            const elemCount = Object.keys(question.configurationAvancee || {}).length + 1;
                            updateConfigurationAvancee(qIndex, `Élément ${elemCount}`, '');
                        }} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-0.5">
                            <Plus size={10} /> Ajouter un élément
                        </button>
                    </div>
                );
            case 'AUDIO/FILE':
                return (
                    <div>
                        <label className="block text-xs font-medium text-gray-700">Contraintes du fichier</label>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-[10px] text-gray-600">Taille max (octets)</label>
                                <input
                                    type="number"
                                    value={question.configurationAvancee?.maxFileSize || 5242880}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value, 10) || 0;
                                        updateConfigurationAvancee(qIndex, 'maxFileSize', val);
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] text-gray-600">Types acceptés (ex: audio/*)</label>
                                <input
                                    type="text"
                                    value={(question.configurationAvancee?.acceptedTypes || ['audio/*']).join(', ')}
                                    onChange={(e) => {
                                        const types = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                                        updateConfigurationAvancee(qIndex, 'acceptedTypes', types);
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                    placeholder="audio/*, video/*"
                                />
                            </div>
                        </div>
                        {!isExamenConcours && renderSolutions()}
                    </div>
                );
            case 'REP_COURT':
            case 'REP_LONG':
                return (
                    <div>
                        {!isExamenConcours && renderSolutions()}
                    </div>
                );
            default:
                return (
                    <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                        Type non reconnu: {type}
                    </div>
                );
        }
    }, [addOptionSomative, addSolution, removeOptionSomative, removeSolution, updateOptionSomative, updateSolution, isCurrentExamenOrConcours]);

    // Rendu Diagnostic (identique, mais on masque resultats pour examen/concours)
    const renderDiagnosticFields = useCallback((question, qIndex) => {
        const isExamenConcours = isCurrentExamenOrConcours;
        return (
            <div className="space-y-2">
                <div>
                    <label className="block text-xs font-medium text-gray-700">Type de question</label>
                    <select
                        value={question.typeQuestion || 'CHOIX_UNIQUE'}
                        onChange={(e) => changeQuestionType(qIndex, e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    >
                        <option value="CHOIX_UNIQUE">Choix unique</option>
                        <option value="CHOIX_MULTIPLE">Choix multiple</option>
                        <option value="VRAI_FAUX">Vrai/Faux</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-700">Options *</label>
                    {question.options?.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center gap-1.5 mb-1">
                            <span className="text-xs font-bold text-gray-400 w-5">
                                {String.fromCharCode(65 + optIndex)}.
                            </span>
                            <input
                                type="text"
                                value={option.texte || ''}
                                onChange={(e) => updateOptionDiagnostic(qIndex, optIndex, 'texte', e.target.value)}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                placeholder={`Option ${optIndex + 1}`}
                            />
                            <button
                                onClick={() => removeOptionDiagnostic(qIndex, optIndex)}
                                className="p-1 text-red-400 hover:bg-red-50 rounded"
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={() => addOptionDiagnostic(qIndex)}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-0.5"
                    >
                        <Plus size={10} /> Ajouter une option
                    </button>
                </div>

                {!isExamenConcours && (
                    <div>
                        <label className="block text-xs font-medium text-gray-700">
                            Réponse(s) correcte(s) (ID des options) - optionnel
                        </label>
                        <input
                            type="text"
                            value={question.resultats || ''}
                            onChange={(e) => updateQuestion(qIndex, 'resultats', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder="opt-1a ou opt-1a,opt-1b"
                        />
                        <p className="text-[9px] text-gray-400 mt-0.5">
                            Pour choix unique: un seul ID. Pour choix multiple: IDs séparés par des virgules.
                            Laissez vide pour correction manuelle.
                        </p>
                    </div>
                )}
            </div>
        );
    }, [addOptionDiagnostic, changeQuestionType, removeOptionDiagnostic, updateOptionDiagnostic, updateQuestion, isCurrentExamenOrConcours]);

    // ============================================================
    // TRANSFORMATION DES QUESTIONS POUR LE BACKEND
    // ============================================================
    const transformQuestionsForBackend = (questions) => {
        const isDiag = evalTypeState === 'DIAGNOSTIC';
        const isExamenConcours = isCurrentExamenOrConcours;
        return questions.map(q => {
            const backendQuestion = {
                type: q.typeQuestion,
                enonce: q.enonce,
                options: [],
                solutions: [],
                indexBonneReponse: null,
                points: q.points || 1,
                explication: q.explication || '',
                feedbackAuto: q.feedbackAuto !== undefined ? q.feedbackAuto : true,
                configurationAvancee: q.configurationAvancee || {}
            };

            if (isDiag) {
                backendQuestion.options = q.options?.map(opt => opt.texte) || [];
                if (!isExamenConcours && q.resultats) {
                    const ids = q.resultats.split(',').map(s => s.trim());
                    const indices = ids.map(id => {
                        const idx = q.options.findIndex(opt => opt.id === id);
                        return idx;
                    }).filter(i => i !== -1);
                    if (q.typeQuestion === 'CHOIX_UNIQUE') {
                        backendQuestion.indexBonneReponse = indices.length > 0 ? indices[0] : null;
                    } else if (q.typeQuestion === 'CHOIX_MULTIPLE') {
                        backendQuestion.solutions = indices.map(i => String(i));
                    }
                }
            } else {
                switch (q.typeQuestion) {
                    case 'CHOIX_UNIQUE':
                    case 'VRAI_FAUX':
                        backendQuestion.options = q.options || [];
                        if (!isExamenConcours && q.solutions && q.solutions.length > 0 && q.solutions[0] !== '') {
                            const correctValue = q.solutions[0];
                            const index = q.options.indexOf(correctValue);
                            if (index !== -1) {
                                backendQuestion.indexBonneReponse = index;
                            } else {
                                backendQuestion.solutions = q.solutions;
                            }
                        }
                        break;
                    case 'CHOIX_MULTIPLE':
                        backendQuestion.options = q.options || [];
                        if (!isExamenConcours) backendQuestion.solutions = q.solutions || [];
                        break;
                    case 'TEXTE_TROU':
                        backendQuestion.options = q.options || [];
                        if (!isExamenConcours) backendQuestion.solutions = q.solutions || [];
                        break;
                    case 'APPARIE':
                        // ⭐ APPARIE ne transporte jamais `solutions` : configurationAvancee
                        // est l'unique source (question + correction).
                        //backendQuestion.options = [];
                        //backendQuestion.solutions = [];
						// ⭐ On génère solutions à partir de configurationAvancee
						//const apparieValues = Object.values(q.configurationAvancee || {});
						backendQuestion.options = [];
						backendQuestion.solutions = Object.values(q.configurationAvancee || {});
                        backendQuestion.configurationAvancee = q.configurationAvancee || {};						
                        break;
                    case 'REP_COURT':
                    case 'REP_LONG':
                        if (!isExamenConcours) backendQuestion.solutions = q.solutions || [];
                        break;
                    case 'CLASSE':
                    case 'CATEGORI':
						//const values = Object.values(q.configurationAvancee || {});
						backendQuestion.options = q.options || [];
						backendQuestion.solutions = Object.values(q.configurationAvancee || {});
						backendQuestion.configurationAvancee = q.configurationAvancee || {};
						break;
                    case 'AUDIO/FILE':
                        if (!isExamenConcours) backendQuestion.solutions = q.solutions || [];
                        break;
                    default:
                        if (!isExamenConcours) backendQuestion.solutions = q.solutions || [];
                }
            }
            return backendQuestion;
        });
    };

    // ============================================================
    // VALIDATION
    // ============================================================
    const validateEvaluation = useCallback((strict = false) => {
        const errors = [];
        const warnings = [];

        if (!evalData.titre || evalData.titre.trim() === '') {
            errors.push('Le titre de l\'évaluation est requis');
        }
        if (!evalData.code || evalData.code.trim() === '') {
            errors.push('Le code de l\'évaluation est requis');
        }
        if (!evalData.questions || evalData.questions.length === 0) {
            errors.push('Au moins une question est requise');
        }

        evalData.questions.forEach((q, idx) => {
            if (!q.enonce || q.enonce.trim() === '') {
                errors.push(`La question ${idx + 1} doit avoir un énoncé`);
            }
            if (evalTypeState === 'DIAGNOSTIC') {
                if (!q.options || q.options.length < 2) {
                    errors.push(`La question ${idx + 1} doit avoir au moins 2 options`);
                }
                if (!isCurrentExamenOrConcours && strict && (!q.resultats || q.resultats.trim() === '')) {
                    errors.push(`La question ${idx + 1} doit avoir une réponse correcte (pour la publication)`);
                }
            } else {
                const hasSolution = q.solutions && q.solutions.length > 0 && q.solutions.some(s => s && String(s).trim() !== '');
                if (!isCurrentExamenOrConcours && strict && !hasSolution) {
                    errors.push(`La question ${idx + 1} doit avoir au moins une solution (pour la publication)`);
                }
                if (['CHOIX_UNIQUE', 'CHOIX_MULTIPLE'].includes(q.typeQuestion)) {
                    if (!q.options || q.options.length < 2) {
                        errors.push(`La question ${idx + 1} doit avoir au moins 2 options`);
                    }
                }
                if (['CLASSE', 'CATEGORI', 'APPARIE'].includes(q.typeQuestion)) {
                    if (!q.configurationAvancee || Object.keys(q.configurationAvancee).length === 0) {
                        errors.push(`La question ${idx + 1} (${getQuestionTypeLabel(q.typeQuestion)}) doit avoir une configurationAvancee non vide — c'est l'unique source de la question et de sa correction pour ce type`);
                    }
                }
                // ⭐ Garde-fou : APPARIE ne doit plus jamais transporter `solutions`.
                // Si ce champ traîne encore (import JSON externe, ancienne donnée), on bloque
                // plutôt que de laisser Evaluation.jsx retomber sur ce fallback.
                if (q.typeQuestion === 'APPARIE' && q.solutions && q.solutions.length > 0) {
                    errors.push(`La question ${idx + 1} (Appariement) contient encore un champ "solutions" — supprimez-le, seule configurationAvancee doit être utilisée`);
                }
            }
        });

        return { errors, warnings };
    }, [evalData, evalTypeState, isCurrentExamenOrConcours]);

    // ============================================================
    // SAUVEGARDE
    // ============================================================
    const handleSave = async () => {
        const effectiveClientId = evalData.clientId || clientId;
        if (!effectiveClientId) {
            const msg = '⚠️ Le clientId est manquant. Veuillez sélectionner un client pour cette évaluation.';
            setError(msg);
            alert(msg);
            return;
        }

        let codeToUse = evalData.code;
        if (!codeToUse || codeToUse.trim() === '') {
            codeToUse = `EVAL-${Date.now()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
            setEvalData(prev => ({ ...prev, code: codeToUse }));
        }

        const isStrict = evalData.status === 'PUBLIEE' || evalData.status === 'TERMINEE';
        const { errors, warnings } = validateEvaluation(isStrict);

        if (errors.length > 0) {
            const msg = `❌ ${errors.join('\n')}`;
            setError(msg);
            alert(msg);
            return;
        }

        if (warnings.length > 0 && evalData.status === 'BROUILLON') {
            const confirmMsg = `⚠️ Certaines questions sont incomplètes :\n${warnings.join('\n')}\n\nSouhaitez-vous quand même sauvegarder en brouillon ?`;
            if (!window.confirm(confirmMsg)) {
                return;
            }
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const payload = {
                titre: evalData.titre,
                code: codeToUse,
                status: evalData.status || 'BROUILLON',
                description: evalData.description || '',
                type: evalTypeState,
                scoreMinimum: evalData.scoreMinimumPourValider || 70,
                questions: transformQuestionsForBackend(evalData.questions),
                estObligatoire: true,
                feedbackAuto: true,
                clientId: effectiveClientId
            };

            const method = evaluationId ? 'PUT' : 'POST';
            const url = evaluationId
                ? `/api/v1/admin/evaluations/${evaluationId}`
                : '/api/v1/admin/evaluations';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
            }

            const saved = await response.json();
            setEvalData(prev => ({ ...prev, ...saved, clientId: effectiveClientId }));
            setSuccess(`✅ Évaluation ${evaluationId ? 'mise à jour' : 'créée'} avec succès`);
            if (onSave) onSave(saved);
        } catch (err) {
            console.error('Erreur sauvegarde :', err);
            setError(`❌ Erreur lors de la sauvegarde : ${err.message}`);
            alert(`❌ Erreur : ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // SUPPRESSION, PRÉVISUALISATION, IMPORT/EXPORT
    // ============================================================
    const handleDelete = async () => {
        if (!evaluationId) return;
        if (!window.confirm('Supprimer définitivement cette évaluation ? Cette action est irréversible.')) return;

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(`/api/v1/admin/evaluations/${evaluationId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
            }

            setSuccess('✅ Évaluation supprimée');
            if (onDelete) onDelete(evaluationId);
        } catch (err) {
            setError(`❌ Erreur lors de la suppression : ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = useCallback(() => {
        const { errors } = validateEvaluation(false);
        if (errors.length > 0) {
            setError(errors.join('\n'));
            return;
        }
        if (onPreview) {
            onPreview(evalData);
        }
    }, [evalData, onPreview, validateEvaluation]);

    const handleExportJson = () => {
        const dataToExport = { ...evalData, clientId: evalData.clientId || clientId };
        const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `evaluation-${evalData.code || 'export'}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setSuccess('✅ Évaluation exportée en JSON');
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleImportJson = () => {
        try {
            const parsed = JSON.parse(importJson);
            if (!parsed.titre || !parsed.questions || !Array.isArray(parsed.questions)) {
                throw new Error('Le JSON doit contenir "titre" et "questions" (tableau).');
            }

            parsed.clientId = parsed.clientId || clientId;
            const importType = parsed.type || evalTypeState;
            const normalizedQuestions = normalizeQuestions(parsed.questions, importType);
            parsed.questions = normalizedQuestions;
            parsed.type = importType;

            setEvalData(prev => ({
                ...prev,
                ...parsed,
                clientId: parsed.clientId,
                questions: normalizedQuestions
            }));
            setEvalTypeState(importType);

            setShowImportModal(false);
            setImportJson('');
            setImportError(null);
            setSuccess('✅ JSON importé avec succès. N\'oubliez pas de sauvegarder !');
            setTimeout(() => setSuccess(null), 4000);
        } catch (err) {
            setImportError(err.message);
        }
    };

    // ============================================================
    // RENDU DES COMPOSANTS (QuestionForm) - Version COMPACTE
    // ============================================================
    const renderQuestionForm = useCallback((question, qIndex) => {
        const isExpanded = expandedQuestions[qIndex] !== undefined ? expandedQuestions[qIndex] : true;
        const isDiag = evalTypeState === 'DIAGNOSTIC';

        return (
            <div key={qIndex} className="border border-gray-200 rounded mb-1.5 overflow-hidden">
                <div
                    className="bg-gray-50 px-3 py-1.5 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors text-sm"
                    onClick={() => toggleQuestionExpand(qIndex)}
                >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-base flex-shrink-0">
                            {isDiag ? '📝' : getQuestionTypeIcon(question.typeQuestion)}
                        </span>
                        <span className="font-semibold text-gray-700 truncate text-xs">
                            Q{qIndex + 1}: {question.enonce || 'Sans énoncé'}
                        </span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">
                            {isDiag
                                ? (question.typeQuestion === 'CHOIX_UNIQUE' ? 'Choix unique' : 'Choix multiple')
                                : getQuestionTypeLabel(question.typeQuestion)
                            }
                        </span>
                        {question.points && (
                            <span className="text-[10px] text-gray-400 flex-shrink-0">
                                {question.points} pt{question.points > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            onClick={(e) => { e.stopPropagation(); removeQuestion(qIndex); }}
                            className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                            <Trash2 size={13} />
                        </button>
                        {isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                    </div>
                </div>

                {isExpanded && (
                    <div className="p-3 space-y-2 bg-white">
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Énoncé *</label>
                            <textarea
                                value={question.enonce || ''}
                                onChange={(e) => updateQuestion(qIndex, 'enonce', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                rows="2"
                                placeholder="Texte de la question"
                            />
                        </div>

                        <div className="p-2 bg-blue-50 rounded border border-blue-200">
                            <label className="block text-xs font-semibold text-blue-700">🔄 Type de question</label>
                            <select
                                value={question.typeQuestion || 'CHOIX_UNIQUE'}
                                onChange={(e) => changeQuestionType(qIndex, e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-blue-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                            >
                                {getAvailableTypes().map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.icon} {type.label}
                                    </option>
                                ))}
                            </select>
                            <p className="text-[9px] text-blue-500 mt-0.5">
                                {isDiag
                                    ? 'Seuls les types "Choix unique", "Choix multiple" et "Vrai/Faux" sont disponibles en diagnostic.'
                                    : 'Choisissez le format de votre question.'}
                            </p>
                        </div>

                        {isDiag
                            ? renderDiagnosticFields(question, qIndex)
                            : renderSomativeFields(question, qIndex)
                        }

                        <div>
                            <label className="block text-xs font-medium text-gray-700">Points</label>
                            <input
                                type="number"
                                value={question.points || 1}
                                onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                min="1"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700">Explication / Feedback</label>
                            <textarea
                                value={question.explication || ''}
                                onChange={(e) => updateQuestion(qIndex, 'explication', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                rows="2"
                                placeholder="Explication de la réponse (optionnelle)"
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }, [expandedQuestions, toggleQuestionExpand, removeQuestion, updateQuestion, evalTypeState, renderDiagnosticFields, renderSomativeFields, getQuestionTypeIcon, getQuestionTypeLabel, changeQuestionType, getAvailableTypes]);

    // ============================================================
    // RENDU PRINCIPAL
    // ============================================================
    const availableTypes = getAvailableTypes();

    return (
        <div className="space-y-3">
            {/* En-tête */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        {evalTypeState === 'DIAGNOSTIC' ? (
                            <BarChart3 size={24} className="text-red-500" />
                        ) : evalTypeState === 'FORMATIVE' ? (
                            <ClipboardList size={24} className="text-amber-500" />
                        ) : evalTypeState === 'EXAMEN' ? (
                            <ClipboardList size={24} className="text-orange-500" />
                        ) : evalTypeState === 'CONCOURS' ? (
                            <ClipboardList size={24} className="text-fuchsia-500" />
                        ) : (
                            <ClipboardList size={24} className="text-purple-500" />
                        )}
                        {evalTypeState === 'DIAGNOSTIC' ? 'Évaluation Diagnostique' :
                         evalTypeState === 'FORMATIVE' ? 'Évaluation Formative' :
                         evalTypeState === 'EXAMEN' ? 'Examen' :
                         evalTypeState === 'CONCOURS' ? 'Concours' : 'Évaluation Sommative'}
                        {evaluationId && <span className="text-sm text-gray-400 font-normal">(ID: {evaluationId})</span>}
                        {clientId && <span className="text-xs text-gray-400 ml-2">Client ID: {clientId}</span>}
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {evalTypeState === 'DIAGNOSTIC'
                            ? 'Questions à choix unique, multiple ou vrai/faux pour évaluer les connaissances initiales'
                            : evalTypeState === 'FORMATIVE'
                            ? 'Questions à choix unique, multiple ou vrai/faux pour un suivi formatif'
                            : 'Tous types de questions pour évaluer les acquis (QCM, Vrai/Faux, Appariement, Classement, Catégorisation, Fichier, etc.)'}
                        {isCurrentExamenOrConcours && ' — Sans solution (correction manuelle)'}
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {evaluationId && (
                        <button onClick={handleDelete} disabled={loading} className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors disabled:opacity-50">
                            🗑️ Supprimer
                        </button>
                    )}
                    <button onClick={() => setShowJsonPreview(!showJsonPreview)} className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded border border-gray-300">
                        {showJsonPreview ? '📝 Éditer' : '👁️ JSON'}
                    </button>
                    <button onClick={handleExportJson} className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded border border-gray-300">
                        ⬇️ Exporter
                    </button>
                    <button onClick={() => setShowImportModal(true)} className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded border border-gray-300">
                        📥 Importer
                    </button>
                    <button onClick={handlePreview} className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors flex items-center gap-1">
                        <Eye size={14} /> Démo
                    </button>
                    <button onClick={handleSave} disabled={loading} className="px-3 py-1 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded transition-colors disabled:opacity-50 flex items-center gap-1">
                        {loading ? 'Sauvegarde...' : '💾 Sauvegarder'}
                    </button>
                </div>
            </div>

            {/* Messages */}
            {error && <div className="p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm whitespace-pre-wrap">{error}</div>}
            {success && <div className="p-2 bg-green-50 border border-green-200 rounded text-green-600 text-sm">{success}</div>}

            {/* Prévisualisation JSON */}
            {showJsonPreview && (
                <div className="border border-gray-300 rounded overflow-hidden">
                    <div className="bg-gray-800 text-white px-3 py-1.5 flex justify-between items-center">
                        <span className="text-sm font-medium">JSON généré</span>
                        <button onClick={() => { navigator.clipboard.writeText(JSON.stringify(evalData, null, 2)); setSuccess('✅ JSON copié'); setTimeout(() => setSuccess(null), 3000); }} className="text-xs text-gray-300 hover:text-white">
                            📋 Copier
                        </button>
                    </div>
                    <pre className="p-3 bg-gray-900 text-green-400 text-xs font-mono overflow-x-auto max-h-64">
                        {JSON.stringify(evalData, null, 2)}
                    </pre>
                </div>
            )}

            {/* Formulaire */}
            {!showJsonPreview && (
                <div className="border border-gray-200 rounded-xl bg-white p-4 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Titre *</label>
                            <input type="text" value={evalData.titre || ''} onChange={(e) => setEvalData(prev => ({ ...prev, titre: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Code</label>
                            <input type="text" value={evalData.code || ''} onChange={(e) => setEvalData(prev => ({ ...prev, code: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Généré auto" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Score minimum (%)</label>
                            <input type="number" value={evalData.scoreMinimumPourValider || 70} onChange={(e) => setEvalData(prev => ({ ...prev, scoreMinimumPourValider: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500" min="0" max="100" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Statut</label>
                            <select value={evalData.status || 'BROUILLON'} onChange={(e) => setEvalData(prev => ({ ...prev, status: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500">
                                <option value="BROUILLON">Brouillon</option>
                                <option value="PUBLIEE">Publiée</option>
                                <option value="TERMINEE">Terminée</option>
                            </select>
                            {evalData.status === 'BROUILLON' && (
                                <p className="text-[10px] text-amber-600 mt-0.5">⚠️ En brouillon, sauvegarde possible même avec réponses incomplètes.</p>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <textarea value={evalData.description || ''} onChange={(e) => setEvalData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500" rows="1" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Type d'évaluation</label>
                            <select
                                value={evalTypeState}
                                onChange={(e) => {
                                    const newType = e.target.value;
                                    setEvalTypeState(newType);
                                    setEvalData(prev => ({
                                        ...prev,
                                        type: newType,
                                        questions: normalizeQuestions(prev.questions, newType)
                                    }));
                                }}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                                <option value="DIAGNOSTIC">Diagnostique</option>
                                <option value="FORMATIVE">Formative</option>
                                <option value="SOMMATIVE">Sommative</option>
                                <option value="EXAMEN">Examen</option>
                                <option value="CONCOURS">Concours</option>
                            </select>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                                {isCurrentExamenOrConcours
                                    ? 'Les examens et concours ne comportent pas de solutions (correction manuelle).'
                                    : 'Les évaluations peuvent avoir des solutions (optionnelles).'}
                            </p>
                        </div>
                        <div className="md:col-span-2 text-[10px] text-gray-400">
                            <span className="font-medium">Client ID :</span> {evalData.clientId || clientId || 'Non défini'}
                        </div>
                    </div>

                    {/* Types de questions disponibles */}
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3">
                        <p className="text-[10px] text-blue-700 font-medium">💡 Types de questions disponibles :</p>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                            {availableTypes.map(type => (
                                <span key={type.value} className="text-[9px] bg-white px-1.5 py-0.5 rounded border border-blue-200 text-blue-600">
                                    {type.icon} {type.label}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Questions */}
                    <div className="border-t border-gray-200 pt-2">
                        <div className="flex items-center justify-between mb-1.5">
                            <h3 className="text-sm font-bold text-gray-700">Questions ({evalData.questions?.length || 0})</h3>
                            <div className="flex gap-1">
                                <button
                                    onClick={toggleAllExpand}
                                    className="px-2 py-0.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-0.5"
                                    title="Tout réduire/étendre"
                                >
                                    <ChevronsUpDown size={12} />
                                </button>
                                <button onClick={addQuestion} className="px-2 py-0.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded flex items-center gap-0.5">
                                    <Plus size={12} /> Ajouter
                                </button>
                            </div>
                        </div>
                        {evalData.questions?.length === 0 && (
                            <div className="text-center py-4 text-gray-400 text-sm">Aucune question. Cliquez sur "Ajouter".</div>
                        )}
                        {evalData.questions?.map((question, qIndex) => (
                            renderQuestionForm(question, qIndex)
                        ))}
                    </div>
                </div>
            )}

            {/* Modale d'import JSON */}
            {showImportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                        <div className="flex items-center justify-between p-3 border-b border-gray-200">
                            <h3 className="text-lg font-bold text-gray-900">Importer un JSON d'évaluation</h3>
                            <button onClick={() => { setShowImportModal(false); setImportError(null); setImportJson(''); }} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">✕</button>
                        </div>
                        <div className="p-4">
                            <textarea value={importJson} onChange={(e) => setImportJson(e.target.value)} placeholder="Collez ici le JSON de votre évaluation..." className="w-full h-48 px-3 py-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                            {importError && <div className="mt-2 text-red-600 text-sm bg-red-50 p-1.5 rounded">❌ {importError}</div>}
                        </div>
                        <div className="flex justify-end gap-2 p-3 border-t border-gray-200">
                            <button onClick={() => { setShowImportModal(false); setImportError(null); setImportJson(''); }} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded transition-colors">Annuler</button>
                            <button onClick={handleImportJson} className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 transition-colors">Valider et importer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}